import { useState, useEffect } from 'react';
import { Container, Col, Row, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { far } from '@fortawesome/pro-regular-svg-icons';
import { injectIntl } from "react-intl";
import Moment from "moment";
import isEmpty from 'lodash.isempty';
import get from 'lodash.get';
import PropTypes from 'prop-types';
import { Helmet } from "react-helmet";
import { messages } from './Job.messages';
import { default as dashBoardMessages } from '../Dashboard/Dashboard.messages';
import { useNavigate, useLocation, useParams, Link } from "react-router-dom";

// services
import { jobGetJob, jobFetchJob, jobGetGovidJob } from '../../services/job/index';
import { paymentService } from '../../services/payment/index';
import { toggleLoading, changeLoadingText } from "../../services/helpers/index";

// cards
import ServingCard from '../../components/ServingCard.js';

// components
import AlertDialog from '../../components/AlertDialog.js';
import CompanyCard from "../../components/CompanyCard";
import CompanyBanner from '../../components/CompanyBanner.js';
import useWhiteLabeling from "../../hooks/useWhiteLabeling";
import InvoiceSummaryCard from '../../components/InvoiceSummaryCard.js';
import InvoiceDetails from '../../components/InvoiceDetails.js';
import SubJobs from '../../components/SubJobs.js';
import CompanyInfoModal from "../../components/CompanyInfoModal";
import useTime from "../../hooks/useTime";
import JobAdditionalActions from "../../components/JobAdditionalActions";
import JobSupport from "../../components/JobSupport";
import useSettings from "../../hooks/useSettings";
import useMessages from "../../hooks/useMessages";
import JobMessages from "../../components/JobMessages";
import useUserAccount from "../../hooks/useUserAccount";
import useSessionStorage from "../../hooks/useSessionStorage";
import useLiveChat from "../../hooks/useLiveChat";
import useJobs from "../../hooks/useJobs";

const Job = (props) => {
	const navigate = useNavigate();
	const location = useLocation();
	const { paymentmethod, uuid } = useParams();
	const lng = props.intl.formatMessage;
	const { userIsAuthenticated, userSignOut, userIsLoggedIn, uuidAuthenticate } = useUserAccount();
	const isAuthed = userIsAuthenticated() && jobGetJob() !== null;
	const [showAlert, setShowAlert] = useState(false);
	const [showAlertMessage, setShowAlertMessage] = useState('');
	const [showAlertType, setShowAlertType] = useState(undefined);
	const [isAuthenticating, setIsAuthenticating] = useState(true);
	const { job, setJob } = useJobs();
	const [disableServingButton, setDisableServingButton] = useState(false);
	const [showServingModal, setShowServingModal] = useState(false);
	const [currency, setCurrency] = useState(process.env.REACT_APP_DEFAULT_CURRENCY);
	const [showCompanyInfoModal, setShowCompanyInfoModal] = useState(false);
	// toggle job details in mobile view
	const [currentIndex, setCurrentIndex] = useState(-1);
	const [currentPage, setCurrentPage] = useState(-1);
	const [lastPage, setLastPage] = useState(-1);
	const [showBrowseButton, setShowBrowseButton] = useState(false);
	const [firstInGovidJobs, setFirstInGovidJobs] = useState(false);
	const [lastInGovidJobs, setLastInGovidJobs] = useState(false);
	const { messageCenterMessages } = useMessages();
	// Wizard
	const [showWizardButton, setShowWizardButton] = useState(false);
	const [isDirectLinkOverpaid, setIsDirectLinkOverpaid] = useState(false);
	const [overpaidInformation, setOverpaidInformation] = useState([]);
	const [showOverpaidInformation, setShowOverpaidInformation] = useState(false);
	const [showOverpaidWizard, setShowOverpaidWizard] = useState(false);

	// Chat
	const { liveChatParameters } = useLiveChat();

	// Take alert-content from location state
	const { message, type } = (location.state?.alert) || {};

	// Message Center
	const [showNewMessageButton, setShowNewMessageButton] = useState(true);
	const [allMessages, setAllMessages] = useState([]);

	// White label
	const { fetchWhiteLabelingData, whiteLabelData, brandingSettings } = useWhiteLabeling(job);
	const { buttonColor, buttonTextColor, logoWidth, logoHeight } = brandingSettings;

	const { addDaysToDate } = useTime();
	const {
		allowServing,
		showChatButton,
		showMessageCenterButton,
		showChatDisabledNotice
	} = useSettings();

	// Support section should be shown, if either the chat or message center is available, and job is other than IL invoice (300)
	const showSupportSection = (showChatButton || showMessageCenterButton) && job?.statuscode !== 300;

	// check if user is authorized to use the message center
	const messageCenterAuthorized = (showMessageCenterButton && isAuthed);

	const { setjobTokenUuid } = useSessionStorage();

	// check if environment allows serving and if job is suitable for it
	const showServing = (allowServing && job?.hasserving && job?.serving_data);

	useEffect(() => {
		// Fetch initial messages when component mounts
		const resolveMessages = () => {
			const jobMessages = messageCenterMessages.filter(msg => msg.jobid === job.jobid.toString());
			setAllMessages(jobMessages);
			if (jobMessages?.length > 0) {
				const isClosed = jobMessages.every(message => (
					message.current_status?.status === 'Closed'
				));
				setShowNewMessageButton(isClosed);
			} else {
				setShowNewMessageButton(true);
			}
		}
		if (!props.maintenance && messageCenterAuthorized && job?.jobid) {
			resolveMessages()
		} else {
			setAllMessages([]);
		}
	}, [props.maintenance, messageCenterMessages, messageCenterAuthorized, job?.jobid]);

	useEffect(() => {
		// Loading animation
		toggleLoading(true, lng(messages.loading) + '...');

		// Show alerts that are being given by previous view
		if (message !== undefined) {
			handleAlertDialog(message, type);
		}

		const redirectForAuth = () => {
			setjobTokenUuid(uuid);
			const alert = { message: lng(messages.authRequired), type: 'info' };
			navigate("/", { state: { alert } });
		}

		const handleJobFetchJobError = (error) => {
			toggleLoading(false);
			if (error.response?.status === 401) {
				userSignOut().then(() => {
					// Redirect to landing page after short delay to allow sign out to complete
					window.setTimeout(redirectForAuth, 50);
				});
			} else {
				// Failed request
				const alert = { message: lng(messages.somethingWentWrong), type: 'danger' };
				navigate("/", { state: { alert } });
			}
		}

		const handleUserLoginError = (_error) => {
			toggleLoading(false);
			// Failed request
			const alert = { message: lng(messages.authError), type: 'danger' };
			navigate("/", { state: { alert } });
		}

		const initJob = () => {
			const job = jobGetJob();

			// Sets a possible new paydate already
			job.newpaydate = addDaysToDate(job.paydate, 14);
			job.firstpaydate = Moment(job.paydate).format('YYYY-MM-DD');

			if (Moment(job.paydate).format('x') < Moment().format('x')) {
				job.firstpaydate = Moment().format('YYYY-MM-DD');
			}

			const jobCurrency = get(job, 'currency', false);

			// Set currency if set on the job data
			if (jobCurrency && jobCurrency !== '') {
				setCurrency(jobCurrency);
			}

			// sets job's details
			setJob(job);
			return job;
		}

		if (uuid) {
			uuidAuthenticate(uuid).then(() => {
				changeLoadingText(lng(messages.searchingForInvoice));

				const handleJobReady = () => {
					const job = initJob();
					// Show wizard if job status is 302 or 104 and account number is not yet given
					if (job.statuscode === 302 || job.statuscode === 104) {
						// Check if bank account number has already been notified
						if (job.modoverpaid !== null && job.modoverpaid) {
							// If IBAN has not been notified, open wizard invoice data step
							setShowOverpaidWizard(true);
							setShowWizardButton(true);
						} else {
							setShowOverpaidWizard(false);
							setShowWizardButton(false);
						}
						setIsDirectLinkOverpaid(true);
					}

					// Forced payment service exists?
					const supportedServices = Object.values(paymentService.PAYMENTMETHODS);

					if (supportedServices.includes(paymentmethod)) {
						window.sessionStorage.setItem('paymentService', paymentmethod);
					} else if (window.sessionStorage.getItem('paymentService') !== null) {
						// If there is no payment service in the url, but session storage
						// value exists, remove values from the session storage
						window.sessionStorage.removeItem('paymentService');
					}
				}

				jobFetchJob().then((_response) => {
					// if jobtype 300 invoice has a follow-up case, redirect to it
					if (_response.redirectdpid) {
						jobFetchJob(_response.redirectdpid)
							.then(handleJobReady)
							.catch(handleJobFetchJobError);
						return;
					}
					handleJobReady();
				}).catch(handleJobFetchJobError);
				
			}).catch(handleUserLoginError);
		} else if (userIsLoggedIn() === false) {
			const alert = { message: lng(messages.authRequired), type: 'primary' };
			navigate("/", { state: { alert } });
		} else if (jobGetJob() === null) {
			const alert = { message: lng(messages.retrySearch), type: 'primary' };
			navigate("/jobsearch", { state: { alert } });
		} else {
			initJob();
		}

		window.scrollTo(0, 0);

	}, []);

	// When job is updated, perform other related actions
	useEffect(() => {
		if (isEmpty(job)) {
			return;
		}
		// Fetch white label data
		fetchWhiteLabelingData().finally(() => {
			toggleLoading(false);
		});

		if (!isAuthed && showServing && job.serving_data?.data?.serving_need_signature) {
			// serving cannot be signed, if user has not logged in
			handleAlertDialog(lng(messages.documentToSign) + ". " + lng(messages.strongAuthenticationNeeded), 'danger');
			setDisableServingButton(true);
		}

		// check if browse button between jobs should be shown
		if (jobGetGovidJob() !== null) {
			browseButtonsDetails();
		}

		if (props.maintenance) {
			handleAlertDialog(lng(messages.underMaintenance), 'warning');
		}

		// allows to view to be rendered
		setIsAuthenticating(false);
	}, [job, props.maintenance]);

	/**
	 * Add details to browse buttons: current page and number of pages altogether.
	 * Also keep track of the index of the current job.
	 */
	function browseButtonsDetails() {
		setFirstInGovidJobs(false);
		setLastInGovidJobs(false);
		const govidJobs = jobGetGovidJob();

		// check the index of currently open job, returns -1 if no matching dpid is found
		const index = govidJobs.findIndex((govidJob) => govidJob.dpid === job.dpid);

		// browse button should not be visible if job is not in the govid job listing
		if (index > -1) {
			// set page numbers and index and save current index
			setCurrentPage(index + 1);
			setLastPage(govidJobs.length);
			setCurrentIndex(index);

			// check if job is first or last (affects e.g. button styling)
			if (index === 0) {
				setFirstInGovidJobs(true);
			}
			if (index === govidJobs.length - 1) {
				setLastInGovidJobs(true);
			}

			// show button for browsing
			setShowBrowseButton(true);
		}
	}

	const handleOverpaidInfo = (wizardOverpaid) => {
		if (wizardOverpaid.show) {
			setOverpaidInformation(wizardOverpaid);
			setShowOverpaidInformation(true);
		}
	}

	/**
	 * Get the previous job from govidjob list.
	 */
	function getPreviousJob() {
		if (currentIndex > 0) {
			handleBrowseJob(jobGetGovidJob()[currentIndex - 1].dpid);
		}
	}

	/**
	 * Get the next job from govidjob list.
	 */
	function getNextJob() {
		const govidJobs = jobGetGovidJob();
		if (currentIndex < govidJobs.length - 1) {
			handleBrowseJob(govidJobs[currentIndex + 1].dpid);
		}
	}

	/**
	 * Browse jobs by fetching the previous or next job from listed govid jobs with dpid.
	 * @param {*} dpid
	 */
	function handleBrowseJob(dpid) {
		toggleLoading(true, lng(messages.searchingForInvoice));
		setShowAlert(false); // hide any remaining error messages
		setDisableServingButton(false); // ensure serving button state is also reset 

		// fetch previous or next job based on the dpid
		jobFetchJob(dpid).then(function () {
			toggleLoading(false);
			const newJob = jobGetJob();

			// Sets a possible new paydate already
			newJob.newpaydate = addDaysToDate(newJob.paydate, 14);
			newJob.firstpaydate = Moment(newJob.paydate).format('YYYY-MM-DD');

			if (Moment(newJob.paydate).format('x') < Moment().format('x')) {
				newJob.firstpaydate = Moment().format('YYYY-MM-DD');
			}

			const jobCurrency = get(newJob, 'currency', false);

			// Set currency if set on the job data
			if (jobCurrency && jobCurrency !== '') {
				setCurrency(jobCurrency);
			}

			setJob(newJob);

		}).catch(function (error) {
			toggleLoading(false);
			if (typeof error?.response !== 'undefined') {
				if (error.response.status === 401) {
					// authentication failed, redirect back to authentication
					const alert = { message: lng(messages.authError), type: 'danger' };
					navigate("/logout", { state: { alert } });
				}
			} else {
				// request failed completely
				handleAlertDialog(lng(messages.somethingWentWrongTryAgain), 'danger');
			}
		});
	}

	/**
	 * Handle alertDialog. Function takes message and type as parameters
	 * and show the alertdialog above all the details related to the invoice.
	 *
	 * @param {string} message
	 * @param {string} type
	 */
	function handleAlertDialog(message, type) {
		setShowAlertMessage(message);
		setShowAlertType(type);
		setShowAlert(true);
	}

	/**
	 * Show serving modal. 
	 * 
	 * Serving modal can be opened from multiple places,
	 * so handle visibility in parent.
	 */
	const handleShowServingModal = () => {
		if (!disableServingButton) {
			setShowServingModal(true);
		}
	};

	/**
	 * Hide serving modal.
	 */
	const handleHideServingModal = () => {
		if (!disableServingButton) {
			setShowServingModal(false);
		}
	};

	const handleCompanyCardClick = () => {
		setShowCompanyInfoModal(true);
	}

	const handleHideCompanyInfoModal = () => {
		setShowCompanyInfoModal(false);
	}

	if (isAuthenticating) {
		return <></>
	}

	// job page (jobs other than overpayment (302) or credit note (104))
	return (
		<div id='job' className='content flex-fill'>
			<Helmet id='job-helmet'>
				<title>{lng(messages.jobid)} {job.jobid.toString()} | MyRopo</title>
			</Helmet>
			<Container id='job-page' className={'mb-2 ' + (isAuthed ? '' : 'mt-4')}>
				{isAuthed && (
					<Row className='align-items-center'>
						<Col xs={10} >
							<Link className='btn to-jobsearch-btn text-start ps-2' to='/jobsearch' id='to-jobsearch' aria-labelledby='to-jobsearch-btn-heading'>
								<h5 id="to-jobsearch-btn-heading">
									<FontAwesomeIcon icon={far.faArrowLeft} /> &nbsp;
									{lng(messages.goBack)}
								</h5>
							</Link>
						</Col>
						{showBrowseButton && (
							<Col xs={{ span: 1, offset: 6 }} xl={{ span: 2, offset: 0 }} className='d-flex justify-content-center justify-content-xl-end'>
								<div className={'d-flex gap-0 browse' + (firstInGovidJobs && lastInGovidJobs ? ' browse-disabled' : '')}>
									<Button className={'btn browse-job previous' + (firstInGovidJobs ? ' browse-disabled' : '')} onClick={() => getPreviousJob()} variant='browse' title={!firstInGovidJobs ? lng(messages.moveToPreviousJob) : ''} aria-label={lng(messages.moveToPreviousJob)}> <FontAwesomeIcon icon={far.faChevronLeft} className='me-2' aria-hidden="true" /> {currentPage}</Button>
									<span>/</span>
									<Button className={'btn browse-job next' + (lastInGovidJobs ? ' browse-disabled' : '')} onClick={() => getNextJob()} variant='browse' title={!lastInGovidJobs ? lng(messages.moveToNextJob) : ''} aria-label={lng(messages.moveToNextJob)}> {lastPage} <FontAwesomeIcon icon={far.faChevronRight} className='ms-2' aria-hidden="true" /></Button>
								</div>
							</Col>
						)}
					</Row>
				)}
				<Row className={'pb-4 m-0 m-sm-1 '}>
					{/*Overpaid information*/}
					{showOverpaidInformation && (
						<AlertDialog
							message={<div>{lng(dashBoardMessages.overpaidInformation1)} <b>{overpaidInformation.account}</b> {lng(dashBoardMessages.overpaidInformation2)} <b>{overpaidInformation.name}</b>. {lng(dashBoardMessages.returnAmount)}: <b>{overpaidInformation.amount}</b>. {lng(dashBoardMessages.refundProcessingFee)}: <b>{overpaidInformation.fee}</b>.</div>}
							type='success'
						/>
					)}
				</Row>
				{/* Alerts and successes to show on the invoice page */}
				<Row className='ps-3 pe-3 '>
					{showAlert &&
						<AlertDialog
							message={showAlertMessage}
							type={showAlertType}
						/>
					}
				</Row>
				<Row>
					{showChatDisabledNotice && (
						<Col xs={12} className='ps-sm-3 pe-sm-3'>
							<AlertDialog
								message={lng(messages.chatTemporarilyUnavailable)}
								type="info"
							/>
						</Col>
					)}
					{/* show info for jobs other than overpayments (302) or credit notes (104) */}
					{job !== null && job.statuscode !== 302 && job.statuscode !== 104 && (
						<Col xs={{ span: 12, order: 1 }} md={{ span: 8, order: 1 }} className='p-0 ps-sm-3 pe-sm-3' >
							<main
								id="main"
								aria-label={job.payee.name + ' ' + lng(messages.invoiceOverview)}
							>
								<CompanyCard
									companyname={job.payee.name}
									data={whiteLabelData}
									logoHeight={logoHeight}
									logoWidth={logoWidth}
									onClick={handleCompanyCardClick}
								/>
								<InvoiceSummaryCard
									job={job}
									toggleLoading={toggleLoading}
									buttonColor={buttonColor}
									buttonTextColor={buttonTextColor}
									currency={currency}
									isAuthed={isAuthed}
									handleAlertDialog={handleAlertDialog}
									paymentMethod={paymentmethod}
									onUpdateJob={setJob}
								/>

								{whiteLabelData && whiteLabelData.banners?.length > 0 && (
									<span className='d-md-none'>
										<CompanyBanner banner={whiteLabelData.banners[0]} />
									</span>
								)}
								<InvoiceDetails
									job={job}
									{...props}
								/>

								{job.members !== null && (
									<SubJobs
										job={job}
										buttonColor={buttonColor}
										buttonTextColor={buttonTextColor}
										{...props}
									/>
								)}
							</main>
						</Col>
					)}
					<Col xs={{ span: 12, order: 2 }} md={{ span: isDirectLinkOverpaid ? 8 : 4, order: 2 }} className={isDirectLinkOverpaid ? '' : 'p-0 ps-sm-3 pe-sm-3 ps-md-0'}>
						{/* show for all jobs */}
						<aside
							aria-label={showSupportSection ? lng(messages.actions) + ' & ' + lng(messages.getSupport) : lng(messages.actions)}
						>
							{job !== null && (
								<>
									{/* Serving info shown in side column, if serving is allowed in the environment and job has serving data*/}
									{showServing && (
										<ServingCard
											job={job}
											servingData={job.serving_data?.data}
											toggleLoading={toggleLoading}
											onUpdateJob={setJob}
											handleAlertDialog={handleAlertDialog}
											onDisableServingButton={setDisableServingButton}
											disableServingButton={disableServingButton}
											showServingModal={showServingModal}
											onShowServingModal={handleShowServingModal}
											onHideServingModal={handleHideServingModal}
											isAuthed={isAuthed}
											{...props}
										/>
									)}
									{whiteLabelData && whiteLabelData.banners?.length > 0 && (
										<span className='d-none d-md-block'>
											<CompanyBanner banner={whiteLabelData.banners[0]} />
										</span>
									)}
									<JobAdditionalActions
										handleAlertDialog={handleAlertDialog}
										toggleLoading={toggleLoading}
										job={job}
										servingButtonDisabled={disableServingButton}
										onShowServingModal={handleShowServingModal}
										showWizardButton={showWizardButton && !overpaidInformation.show}
										showServing={showServing && job.serving_data?.data?.serving_need_signature}
										isDirectLinkOverpaid={isDirectLinkOverpaid}
										showOverpaidWizard={showOverpaidWizard}
										setShowOverpaidWizard={setShowOverpaidWizard}
										onOverpaidInfo={handleOverpaidInfo}
									/>
								</>
							)}
							{showSupportSection && job !== null && (
								<JobSupport
									showMessageCenterButton={showMessageCenterButton}
									showChatButton={showChatButton}
									showChatDisabledNotice={showChatDisabledNotice}
									showNewMessageButton={showNewMessageButton}
									livechatParameters={liveChatParameters}
									messageCenterAuthorized={messageCenterAuthorized}
									maintenance={props.maintenance}
									toggleLoading={toggleLoading}
									job={job}
								/>
							)}
						</aside>
						{allMessages.length > 0 && (
							<aside
								aria-label={lng(messages.messagesRelatedToJob)}
								aria-live='polite'
							>
								<JobMessages
									messages={allMessages}
									title={lng(messages.messageCenterMessages)}
								/>
							</aside>
						)}
					</Col>
				</Row>
			</Container>
			<CompanyInfoModal
				companyName={job.payee?.name}
				onHide={handleHideCompanyInfoModal}
				show={showCompanyInfoModal}
				data={whiteLabelData}
				logoHeight={logoHeight}
				logoWidth={logoWidth}
			/>
		</div>
	);
};

Job.propTypes = {
	'intl': PropTypes.object.isRequired,
	'maintenance': PropTypes.bool.isRequired
};

export default injectIntl(Job);
